import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline, useMediaQuery } from '@mui/material';
import { createAppTheme } from '../theme/createAppTheme';
import { readThemeMode, persistThemeMode, type ThemeMode } from '../theme/preference';

type ResolvedThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  resolvedMode: ResolvedThemeMode;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * ThemeProvider - Manages light/dark mode theme switching
 *
 * Features:
 * - Persists theme preference to localStorage
 * - Provides theme toggle function
 * - Supports auto mode that follows system preferences
 * - Creates MUI theme based on current mode
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Detect system preference
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)', {
    noSsr: true,
  });

  // Initialize theme from localStorage or default to auto
  const [mode, setMode] = useState<ThemeMode>(readThemeMode);

  // Persist theme changes to localStorage
  useEffect(() => {
    persistThemeMode(mode);
  }, [mode]);

  // Resolve the actual theme mode (auto -> light/dark based on system)
  const resolvedMode: ResolvedThemeMode = useMemo(() => {
    if (mode === 'auto') {
      return prefersDarkMode ? 'dark' : 'light';
    }
    return mode;
  }, [mode, prefersDarkMode]);

  const toggleTheme = () => {
    setMode((prevMode) => {
      if (prevMode === 'light') return 'dark';
      if (prevMode === 'dark') return 'auto';
      return 'light'; // auto -> light
    });
  };

  const setThemeMode = (newMode: ThemeMode) => {
    setMode(newMode);
  };

  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  const value = useMemo(
    () => ({
      mode,
      resolvedMode,
      toggleTheme,
      setThemeMode,
    }),
    [mode, resolvedMode],
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
